import { config } from '../../config/config';
import authService from '../../services/authService';

const IMPERSONATION_TOKEN_KEY = 'impersonation_token';
const ORIGINAL_USER_KEY = 'impersonation_original_user';

class ImpersonationManager {
  isImpersonating(): boolean {
    return !!localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  }

  getImpersonationToken(): string | null {
    return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  }

  async startImpersonation(userId: string, tenantId?: string, landingPath: string = '/dashboard'): Promise<void> {
    // Get the caller's current access token for validation
    const callerToken = await authService.getAccessToken();
    if (!callerToken) {
      throw new Error('Not authenticated');
    }

    // Call the OpenIddict token endpoint with the Impersonation grant type
    const tokenUrl = `${config.auth.authority}connect/token`;
    const params: Record<string, string> = {
      grant_type: 'Impersonation',
      client_id: config.auth.clientId,
      token: callerToken,
      user_id: userId,
      scope: 'openid profile email offline_access Routemize',
    };
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const body = new URLSearchParams(params);

    // Include tenant header for multi-tenancy resolution
    const headers: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    const storedConfig = localStorage.getItem('app_config');
    if (storedConfig) {
      try {
        const tenantName = JSON.parse(storedConfig)?.currentTenant?.name;
        if (tenantName) headers['__tenant'] = tenantName;
      } catch { /* ignore */ }
    }

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers,
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error_description || errorData?.error || 'Impersonation failed');
    }

    const tokenResponse = await response.json();
    const accessToken = tokenResponse.access_token;

    if (!accessToken) {
      throw new Error('No access token in response');
    }

    // Save original user info for display in banner when reverting
    const currentUser = await authService.getUser();
    if (currentUser) {
      localStorage.setItem(ORIGINAL_USER_KEY, JSON.stringify({
        name: currentUser.profile?.name || currentUser.profile?.preferred_username,
        sub: currentUser.profile?.sub,
      }));
    }

    // Store impersonation token
    localStorage.setItem(IMPERSONATION_TOKEN_KEY, accessToken);

    // Clear cached ABP config so it re-fetches with new token
    localStorage.removeItem('app_config');
    localStorage.removeItem('app_config_version');

    // Navigate to the landing path. Defaults to the dashboard; partner
    // impersonation lands on /admin so AdminIndexRoute routes to the portal.
    window.location.href = landingPath;
  }

  stopImpersonation(): void {
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    localStorage.removeItem('app_config');
    localStorage.removeItem('app_config_version');
    window.location.reload();
  }

  getOriginalUser(): { name: string; sub: string } | null {
    const stored = localStorage.getItem(ORIGINAL_USER_KEY);
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  getTokenExpiry(): Date | null {
    const token = this.getImpersonationToken();
    if (!token) return null;
    try {
      // Token might be an opaque Data Protection token, not a JWT
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch {
      // Token is opaque (encrypted) — can't decode expiry
      return null;
    }
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false; // Can't determine — assume valid
    return expiry <= new Date();
  }
}

export const impersonationManager = new ImpersonationManager();
